import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  structuredData?: object;
}

const SEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData
}: SEOProps) => {
  const fullTitle = title.includes('Utah Whiskey Tracker') 
    ? title 
    : `${title} | Utah Whiskey Tracker`;
  
  const defaultOgImage = '/lovable-uploads/2655d58e-796b-4804-9756-b681d8284377.png';
  
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Update or create meta tags
    const updateOrCreateMeta = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    // Basic meta tags
    updateOrCreateMeta('description', description);
    if (keywords) updateOrCreateMeta('keywords', keywords);
    updateOrCreateMeta('robots', 'index, follow');
    updateOrCreateMeta('author', 'Whiskey Tracker, LLC');
    
    // Open Graph tags
    updateOrCreateMeta('og:title', ogTitle || fullTitle, true);
    updateOrCreateMeta('og:description', ogDescription || description, true);
    updateOrCreateMeta('og:type', ogType, true);
    updateOrCreateMeta('og:image', ogImage || defaultOgImage, true);
    updateOrCreateMeta('og:site_name', 'Utah Whiskey Tracker', true);
    
    // Twitter tags
    updateOrCreateMeta('twitter:card', twitterCard);
    updateOrCreateMeta('twitter:title', ogTitle || fullTitle);
    updateOrCreateMeta('twitter:description', ogDescription || description);
    updateOrCreateMeta('twitter:image', ogImage || defaultOgImage);
    
    // Canonical link
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }
    
    // Structured data
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }
  }, [fullTitle, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, twitterCard, structuredData]);
  
  return null; // This component doesn't render anything
};

export default SEO;
